/* app/bi-profile.jsx - Business Profile page (single owner entity).
   Wired to /api/bi/entities (owner_flag=1) + extract-from-url (is_own_site);
   the "ours" products/industries are is_ours entities. Market constrains everything.
   Per-section AI "Fetch from page" (offerings) defers to Profile 2. */
import { api } from '../lib/api.js';
import { newRunId, pollRun } from '../lib/run.js';
(function () {
  const { useState, useEffect, useCallback } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }

  const MARKET_LANGUAGES = {
    "France": "French", "Germany": "German", "Spain": "Spanish", "Italy": "Italian",
    "Ireland": "English", "United Kingdom": "English",
    "United States of America": "English", "United Arab Emirates": "Arabic",
  };
  const MARKETS = Object.keys(MARKET_LANGUAGES);

  function ChipRow({ items, onRemove, onAdd, placeholder, lead }) {
    const [val, setVal] = useState("");
    return (
      <div className="bp-chips">
        {items.map(t => <span className="bp-chip" key={t}>{lead && <I name={lead} size={13} className="bp-lead" />}{t}<span className="bp-chip__x" onClick={() => onRemove(t)}><I name="x" size={13} /></span></span>)}
        <span className="bp-chip-add">
          <button type="button" className="bp-chip-add__btn" title={placeholder} aria-label={placeholder} onClick={() => { if (val.trim()) { onAdd(val.trim()); setVal(""); } }}><I name="plus" size={15} /></button>
          <input value={val} placeholder={placeholder} onChange={e => setVal(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && val.trim()) { onAdd(val.trim()); setVal(""); } }} />
        </span>
      </div>
    );
  }

  function BusinessProfile({ site }) {
    const DS = window.HuddleDesignSystem_775113;
    const { Input, Select, Textarea } = DS;
    const siteId = site && (site.site_id != null ? site.site_id : site.id);

    const [ownerId, setOwnerId] = useState(null);
    const [name, setName] = useState("");
    const [url, setUrl] = useState("");
    // A business rarely lists everything it sells on one page, so this is a
    // list. Always at least one row so the field is visible when empty.
    const [productsUrls, setProductsUrls] = useState([""]);
    const [market, setMarket] = useState("");
    const [siteLang, setSiteLang] = useState("");
    const [contentLang, setContentLang] = useState("");
    const [blogs, setBlogs] = useState("");
    const [positioning, setPositioning] = useState("");
    const [prodEnts, setProdEnts] = useState([]);   // [{id,name}]
    const [indEnts, setIndEnts] = useState([]);
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);

    const loadOwner = useCallback(async () => {
      if (siteId == null) return;
      try {
        const rows = await api(`/bi/entities?site_id=${siteId}&owner_flag=1`);
        const o = Array.isArray(rows) && rows[0];
        if (o) {
          const d = o.data || {};
          setOwnerId(o.id); setName(o.name || ""); setUrl(o.url || "");
          setMarket(d.market || ""); setSiteLang(d.site_language || ""); setContentLang(d.content_language || "");
          setPositioning(d.positioning || "");
          // products_urls is the list; products_url is the single-page field it
          // grew out of, still written on save and still read by older screens.
          const list = Array.isArray(d.products_urls) ? d.products_urls.filter(Boolean)
            : (d.products_url ? [d.products_url] : []);
          setProductsUrls(list.length ? list : [""]);
          setBlogs(Array.isArray(d.blog_urls) ? d.blog_urls.join("\n") : (d.blog_urls || ""));
        }
      } catch { /* none yet */ }
    }, [siteId]);

    const loadChips = useCallback(async () => {
      if (siteId == null) return;
      try {
        const [p, i] = await Promise.all([
          api(`/bi/entities?site_id=${siteId}&entity_type=product&is_ours=1`).catch(() => []),
          api(`/bi/entities?site_id=${siteId}&entity_type=industry&is_ours=1`).catch(() => []),
        ]);
        setProdEnts((Array.isArray(p) ? p : []).map(x => ({ id: x.id, name: x.name })));
        setIndEnts((Array.isArray(i) ? i : []).map(x => ({ id: x.id, name: x.name })));
      } catch { /* */ }
    }, [siteId]);

    useEffect(() => { loadOwner(); loadChips(); }, [loadOwner, loadChips]);

    const onMarket = (m) => { setMarket(m); const lang = MARKET_LANGUAGES[m]; if (lang) { setSiteLang(lang); setContentLang(lang); } };

    // ── Products / services pages ────────────────────────────
    // Deduped and trimmed on the way out, so a blank row the user never filled
    // and a page pasted twice never reach the reader or the saved profile.
    const cleanProductsUrls = () => [...new Set(productsUrls.map(u => u.trim()).filter(Boolean))];
    const setProductsUrlAt = (i, v) => setProductsUrls(list => list.map((u, k) => (k === i ? v : u)));
    const addProductsUrl = () => setProductsUrls(list => [...list, ""]);
    const removeProductsUrlAt = (i) => setProductsUrls(list => {
      const next = list.filter((_, k) => k !== i);
      return next.length ? next : [""];   // never leave the field with no row
    });

    const save = async () => {
      if (!name.trim()) { setErr("Business name is required."); return; }
      if (!market) { setErr("Market / geography is required."); return; }
      if (siteId == null) { setErr("No active site."); return; }
      setSaving(true); setErr(null); setStatus(null);
      const payload = {
        site_id: siteId, entity_type: "product", owner_flag: 1,
        name: name.trim(), url: url.trim() || null,
        data: {
          market, site_language: siteLang, content_language: contentLang, positioning,
          blog_urls: blogs.split(/[\n,]/).map(s => s.trim()).filter(Boolean),
          products_urls: cleanProductsUrls(),
          // Kept in step with the first page: the legacy Business Profile and
          // the SEO Products screen still read this single field.
          products_url: cleanProductsUrls()[0] || "",
          pricing: "", relationships_to_me: [],
        },
        source: "manual", validation_status: "validated",
      };
      try {
        if (ownerId) await api(`/bi/entities/${ownerId}`, { method: "PATCH", body: JSON.stringify(payload) });
        else { const c = await api(`/bi/entities`, { method: "POST", body: JSON.stringify(payload) }); setOwnerId(c && c.id); }
        setStatus("saved");
      } catch (e) { setErr(e.message || "Save failed"); }
      finally { setSaving(false); }
    };

    const autofill = async () => {
      if (!url.trim()) { setErr("Enter your business website first."); return; }
      if (siteId == null) { setErr("No active site."); return; }
      setBusy(true); setErr(null);
      try {
        const res = await api(`/bi/extract-from-url`, { method: "POST", body: JSON.stringify({ site_id: siteId, url: url.trim(), is_own_site: true }) });
        const ex = res && res.extracted;
        if (ex) {
          if (ex.name) setName(ex.name);
          if (ex.url) setUrl(ex.url);
          if (ex.positioning) setPositioning(ex.positioning);
          if (ex.market && MARKET_LANGUAGES[ex.market]) onMarket(ex.market);
          setStatus("autofilled");
        }
      } catch (e) { setErr(e.message || "Fetch failed"); }
      finally { setBusy(false); }
    };

    const addEnt = async (type, nm, refetch) => {
      if (siteId == null) return;
      try { await api(`/bi/entities`, { method: "POST", body: JSON.stringify({ site_id: siteId, entity_type: type, name: nm, is_ours: 1, data: {}, source: "manual", validation_status: "validated" }) }); refetch(); } catch { /* */ }
    };
    const removeEnt = async (id, refetch) => {
      if (siteId == null || id == null) return;
      try { await api(`/bi/entities/${id}?site_id=${siteId}`, { method: "DELETE" }); refetch(); } catch { /* */ }
    };
    const removeByName = (ents, nm, refetch) => { const e = ents.find(x => x.name === nm); if (e) removeEnt(e.id, refetch); };

    // ── Fetch from page ──────────────────────────────────────
    // POST /bi/extract-list reads a page and returns the offerings/segments the
    // business itself provides. It deliberately does NOT save: the user picks
    // which to keep, so an AI misread never silently lands in the profile.
    // Products read the products/services page (falling back to the site root);
    // industries read the site root, where positioning copy usually lives.
    const [pick, setPick] = useState(null);   // { type, items:[{name,summary}], chosen:Set, busy }
    const [fetching, setFetching] = useState(null);   // "product" | "industry"
    const [fetchRun, setFetchRun] = useState(null);   // server-reported progress

    const fetchList = async (type) => {
      // Products read every products/services page on the profile (falling back
      // to the site root); industries read the site root, where positioning
      // copy usually lives.
      const srcs = type === "product"
        ? (cleanProductsUrls().length ? cleanProductsUrls() : [url.trim()].filter(Boolean))
        : [url.trim()].filter(Boolean);
      if (!srcs.length) { setErr(type === "product"
        ? "Add your products/services webpage (or business website) above first."
        : "Add your business website above first."); return; }
      if (siteId == null) { setErr("No active site."); return; }
      setFetching(type); setErr(null); setFetchRun(null);
      // Reading several pages is real, countable work, so the button says which
      // page it is on rather than spinning for however long it takes.
      const runId = newRunId();
      const stop = pollRun(runId, setFetchRun);
      try {
        const res = await api(`/bi/extract-list`, { method: "POST", body: JSON.stringify({ run_id: runId, site_id: siteId, urls: srcs, entity_type: type }) });
        const items = (res && Array.isArray(res.items) ? res.items : []).filter(x => x && x.name);
        const sources = (res && Array.isArray(res.sources)) ? res.sources : srcs.map(u => ({ url: u, ok: true }));
        const failed = sources.filter(s => !s.ok);
        if (!items.length) {
          setErr(`Nothing found on ${srcs.length === 1 ? srcs[0] : `those ${srcs.length} pages`}. Try a page that lists your ${type === "product" ? "products/services" : "sectors"}.`);
          return;
        }
        // A page that could not be read is named rather than passed over: the
        // list is incomplete and the user is the one who can fix the URL.
        if (failed.length) setErr(`Could not read ${failed.map(f => f.url).join(", ")}. The list below is from the ${sources.length - failed.length} page${sources.length - failed.length === 1 ? "" : "s"} that did read.`);
        // Pre-select only what isn't already a chip, so re-running is additive.
        const existing = new Set((type === "product" ? prodEnts : indEnts).map(e => e.name.toLowerCase()));
        setPick({ type, sources, items, chosen: new Set(items.map(x => x.name).filter(n => !existing.has(n.toLowerCase()))) });
      } catch (e) { setErr(e.message || "Fetch failed"); }
      finally { stop(); setFetching(null); setFetchRun(null); }
    };

    const confirmPick = async () => {
      if (!pick) return;
      const chosen = pick.items.filter(x => pick.chosen.has(x.name));
      const type = pick.type;
      setPick(p => ({ ...p, busy: true }));
      for (const item of chosen) await addEnt(type, item.name, () => {});
      await loadChips();
      setPick(null);
      setStatus(`added-${type}`);
    };

    const FetchFromPageBtn = ({ type }) => (
      <button className="bi-btn bi-btn--sm bi-btn--ai" disabled={fetching === type}
              title={type === "product"
                ? "Read your products/services page and pick what to add"
                : "Read your website and pick the sectors to add"}
              onClick={() => fetchList(type)}>
        {fetching === type
          ? <span className="ml-spinner ml-spinner--sm ml-spinner--ai" style={{ width: 14, height: 14 }} />
          : <I name="sparkles" size={15} />}
        {fetching !== type ? "Fetch from page"
          : fetchRun ? `${fetchRun.label}${fetchRun.note ? ` · ${fetchRun.note}` : ""}`
          : "Reading…"}
      </button>
    );

    const togglePick = (nm) => setPick(p => {
      const chosen = new Set(p.chosen);
      if (chosen.has(nm)) chosen.delete(nm); else chosen.add(nm);
      return { ...p, chosen };
    });

    return (
      <div className="bp-page">
        <h1 className="bi-title"><span className="bi-title__icon"><I name="briefcase" size={20} /></span>Business Profile</h1>
        <p className="bp-lede">The single owner-entity every BI module is anchored to. Your <b>Market</b> constrains everything: competitors, products, industries and partners are all discovered and scored relative to it.</p>

        <div className="bp-section">
          <div className="bp-section__h">
            <div className="bp-section__title">Identity</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="bi-btn bi-btn--sm bi-btn--ai" disabled={busy} onClick={autofill}>{busy ? <span className="ml-spinner ml-spinner--sm ml-spinner--ai" style={{ width: 14, height: 14 }} /> : <I name="sparkles" size={15} />}Fetch &amp; autofill</button>
              <button className="bi-btn bi-btn--sm bi-btn--primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save profile"}</button>
            </div>
          </div>
          <div className="bp-section__body">
            <div className="bp-grid">
              <div><div className="bp-label">Business name <span className="bp-label__req">*</span></div><Input value={name} onChange={e => setName(e.target.value)} placeholder="Your business name" /></div>
              <div><div className="bp-label">Business website</div><Input leadingIcon="globe" value={url} onChange={e => setUrl(e.target.value)} placeholder="example.com" /></div>
              <div className="bp-full">
                <div className="bp-label">Products / services webpage{productsUrls.length > 1 ? "s" : ""}</div>
                {productsUrls.map((u, i) => (
                  <div className="bp-urlrow" key={i}>
                    <Input leadingIcon="link" value={u}
                           onChange={e => setProductsUrlAt(i, e.target.value)}
                           placeholder={i === 0 ? "example.com/services" : "example.com/services/consulting"} />
                    {i === 0 ? (
                      <button type="button" className="bp-urlrow__btn" title="Add another products / services page"
                              aria-label="Add another products / services page" onClick={addProductsUrl}>
                        <I name="plus" size={15} />
                      </button>
                    ) : (
                      <button type="button" className="bp-urlrow__btn bp-urlrow__btn--rm" title="Remove this page"
                              aria-label={`Remove page ${i + 1}`} onClick={() => removeProductsUrlAt(i)}>
                        <I name="x" size={15} />
                      </button>
                    )}
                  </div>
                ))}
                <div className="bp-hint">
                  {productsUrls.length > 1
                    ? `AI reads all ${cleanProductsUrls().length || productsUrls.length} pages when you fetch products below, and merges what it finds.`
                    : "AI reads your offerings from this page when you fetch products below. Use + to add more pages."}
                </div>
              </div>
              <div className="bp-full bp-market">
                <div className="bp-market__head"><I name="globe" size={16} style={{ color: "var(--indigo-600)" }} /><span style={{ fontWeight: 600, color: "var(--text-primary)" }}>Market / geography</span><span className="bp-label__req">*</span><span className="bp-market__badge">Constrains everything</span></div>
                <div className="bp-grid">
                  <div><div className="bp-label">Country</div><Select value={market} onChange={e => onMarket(e.target.value)}><option value="">Select a country…</option>{MARKETS.map(m => <option key={m} value={m}>{m}</option>)}</Select></div>
                  <div></div>
                  <div><div className="bp-label">Business site language</div><Input value={siteLang} onChange={e => setSiteLang(e.target.value)} /><div className="bp-hint">Auto-filled from market · editable</div></div>
                  <div><div className="bp-label">Content writing language</div><Input value={contentLang} onChange={e => setContentLang(e.target.value)} /><div className="bp-hint">Auto-filled from market · editable</div></div>
                </div>
              </div>
              <div className="bp-full"><div className="bp-label">Blog / site URLs</div><Textarea rows={3} value={blogs} onChange={e => setBlogs(e.target.value)} placeholder={"example.com/blog\nexample.com/guides"} /><div className="bp-hint">One per line.</div></div>
              <div className="bp-full"><div className="bp-label">Positioning</div><Textarea rows={2} value={positioning} onChange={e => setPositioning(e.target.value)} placeholder="One-line positioning statement" /></div>
            </div>
            {err && <div className="bp-status bp-status--info" style={{ color: "var(--danger)" }}><I name="alert-triangle" size={16} />{err}</div>}
            {status === "autofilled" && <div className="bp-status bp-status--info"><I name="info" size={16} />Autofilled from your website - review the <b>&nbsp;Market&nbsp;</b> before saving.</div>}
            {status === "saved" && <div className="bp-status bp-status--ok"><I name="check-circle" size={16} />Saved - all suggestions now concentrate on <b>&nbsp;{market}</b>.</div>}
            {status === "added-product" && <div className="bp-status bp-status--ok"><I name="check-circle" size={16} />Products added - they roll up into the Products module as ★ ours.</div>}
            {status === "added-industry" && <div className="bp-status bp-status--ok"><I name="check-circle" size={16} />Industries added - they roll up into the Industries module as ★ ours.</div>}
          </div>
        </div>

        <div className="bp-section">
          <div className="bp-section__h">
            <div><div className="bp-section__title">Our products</div><div className="bp-section__sub">Roll up into the Products module as ★ ours.</div></div>
            <FetchFromPageBtn type={"product"} />
          </div>
          <div className="bp-section__body">
            <ChipRow items={prodEnts.map(e => e.name)} lead="box" placeholder="Add a product" onAdd={p => addEnt("product", p, loadChips)} onRemove={p => removeByName(prodEnts, p, loadChips)} />
          </div>
        </div>

        <div className="bp-section">
          <div className="bp-section__h">
            <div><div className="bp-section__title">Our industries</div><div className="bp-section__sub">The segments you operate in.</div></div>
            <FetchFromPageBtn type={"industry"} />
          </div>
          <div className="bp-section__body">
            <ChipRow items={indEnts.map(e => e.name)} lead="globe" placeholder="Add an industry" onAdd={p => addEnt("industry", p, loadChips)} onRemove={p => removeByName(indEnts, p, loadChips)} />
          </div>
        </div>

        {pick && React.createElement(window.MlBiModals._Modal, {
          title: pick.type === "product" ? "Products found on your page" : "Industries found on your page",
          sub: (() => {
            const ok = (pick.sources || []).filter(s => s.ok);
            const read = ok.length === 1 ? `Read from ${ok[0].url}` : `Read from ${ok.length} pages`;
            return `${read}. Pick what to keep, nothing is saved until you confirm.`;
          })(),
          onClose: pick.busy ? () => {} : () => setPick(null),
          foot: <>
            <button className="bi-btn" onClick={() => setPick(null)} disabled={pick.busy}>Cancel</button>
            <button className="bi-btn bi-btn--primary" disabled={pick.busy || pick.chosen.size === 0} onClick={confirmPick}>
              {pick.busy ? "Adding…" : `Add ${pick.chosen.size} ${pick.type === "product" ? "product" : "industry"}${pick.chosen.size === 1 ? "" : pick.type === "product" ? "s" : " sectors"}`}
            </button>
          </>,
        }, (
          <div className="bi-modal__body" style={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: 420, overflowY: "auto" }}>
            {pick.items.map((it) => (
              <label key={it.name} className="bp-pick">
                <input type="checkbox" checked={pick.chosen.has(it.name)} onChange={() => togglePick(it.name)} disabled={pick.busy} />
                <span className="bp-pick__text">
                  <span className="bp-pick__name">{it.name}</span>
                  {it.summary && <span className="bp-pick__sub">{it.summary}</span>}
                </span>
              </label>
            ))}
          </div>
        ))}
      </div>
    );
  }

  window.MlBiProfile = { BusinessProfile };
})();
